import React, { useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Calculator, Hotel, AlertTriangle, CheckCircle, Clock, Info, Building2, Save, FileDown, FileText, Users, CalendarCheck, History, Trash2, Edit, FolderOpen, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to convert UTC date to Turkey date string
const utcToTurkeyDate = (isoDateString) => {
  if (!isoDateString) return "";
  if (!isoDateString.includes('T') && !isoDateString.includes('Z')) {
    return isoDateString.split(' ')[0];
  }
  try {
    const date = new Date(isoDateString);
    return date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
  } catch (e) {
    return isoDateString.split('T')[0];
  }
};

const ManDayCalculatorPage = () => {
  const { user } = useContext(AuthContext);
  const { tenantSlug } = useTenant();
  
  // Form state
  const [roomCount, setRoomCount] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [riskLevel, setRiskLevel] = useState("low_risk");
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  
  // FR.02 Table 1 - Pre-Questions (Ön Sorular)
  const [preQuestions, setPreQuestions] = useState({
    certificateWithdrawn: null,      // GSTC sertifikası iptal edildi mi?
    certificateSuspended: null,      // GSTC sertifikası askıya alındı mı?
    associationWithBanned: null,     // Yasaklı kuruluşlarla ilişki var mı?
    litigationOrViolation: null      // Dava veya ihlal var mı?
  });
  
  // FR.02 Table 2 - Additional Fields
  const [siteType, setSiteType] = useState("single"); // single, multi, group
  const [commercialName, setCommercialName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [headOfficeAddress, setHeadOfficeAddress] = useState("");
  const [siteAddresses, setSiteAddresses] = useState("");
  const [shiftCount, setShiftCount] = useState("1");  // Vardiya sayısı
  const [sameActivityAllShifts, setSameActivityAllShifts] = useState(true);  // Tüm vardiyalarda aynı faaliyet?
  const [sameActivityAllSites, setSameActivityAllSites] = useState(true);    // Tüm şubelerde aynı faaliyet?
  const [auditLanguage, setAuditLanguage] = useState("Turkish");
  const [documentLanguage, setDocumentLanguage] = useState("English");  // Döküman dili
  const [gstcStandardScope, setGstcStandardScope] = useState("GSTC Standard for Hotels & Accommodations V3.0");
  const [legalContext, setLegalContext] = useState("");
  const [auditType, setAuditType] = useState("initial");
  
  // FR.02 Table 5 - Extremely Low Risk Factors
  const [extremelyLowRiskFactors, setExtremelyLowRiskFactors] = useState({
    lessThan20Rooms: false,
    noConferenceVenue: false,
    localResident: false,
    notInSensitiveArea: false
  });
  
  // FR.02 Table 9 - Audit Team
  const [teamLeader, setTeamLeader] = useState("");
  const [auditors, setAuditors] = useState("");
  const [technicalExpert, setTechnicalExpert] = useState("");
  const [observer, setObserver] = useState("");
  
  // FR.02 Table 9 - Audit Dates
  const [initialCertDate, setInitialCertDate] = useState("");
  const [surveillance1Date, setSurveillance1Date] = useState("");
  const [surveillance2Date, setSurveillance2Date] = useState("");
  const [recertDate, setRecertDate] = useState("");
  
  // FR.02 Table 9 - Evaluation (Değerlendirme)
  const [evaluation, setEvaluation] = useState({
    relatedRegulations: "conform",           // İlgili düzenlemeler: conform/non-conform
    smsConformity: "conform",                // SMS: conform/non-conform
    groupManagerAppointment: "conform",      // Grup yöneticisi atama: conform/non-conform
    canBeProposed: true,                     // Önerilebilir mi: true/false
    explanation: ""                          // Açıklama
  });
  
  // FR.02 Table 9 - Approval & Review
  const [approvedBy, setApprovedBy] = useState("");
  const [approvedDate, setApprovedDate] = useState("");
  const [reviewedBy, setReviewedBy] = useState("");
  const [reviewedDate, setReviewedDate] = useState("");
  
  // Data state
  const [riskFactors, setRiskFactors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [auditorsList, setAuditorsList] = useState([]); // Kayıtlı denetçiler
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  
  // Manual entry mode for auditors
  const [manualTeamLeader, setManualTeamLeader] = useState(false);
  const [manualAuditor, setManualAuditor] = useState(false);
  const [manualTechExpert, setManualTechExpert] = useState(false);
  const [manualObserver, setManualObserver] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [loadingFactors, setLoadingFactors] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingFR02, setDownloadingFR02] = useState(false);
  const [showFR02Dialog, setShowFR02Dialog] = useState(false);
  
  // Saved Calculations State
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [loadingSavedCalcs, setLoadingSavedCalcs] = useState(false);
  const [showSavedCalcsDialog, setShowSavedCalcsDialog] = useState(false);
  const [currentCalculationId, setCurrentCalculationId] = useState(null);
  const [savingCalculation, setSavingCalculation] = useState(false);
  const [calculationNotes, setCalculationNotes] = useState("");

  // TL.01 Adam-Gün Tablosu
  const mandayTable = {
    "high_risk": { "1-40": 1.5, "41-100": 2.0, "101-200": 2.5, "201-500": 3.0, "501-999": 3.5, "1000+": 4.0 },
    "low_risk": { "1-40": 1.5, "41-100": 1.5, "101-200": 2.0, "201-500": 2.5, "501-999": 3.0, "1000+": 3.5 },
    "extremely_low_risk": { "1-40": 1.0, "41-100": 1.0 }
  };

  // Fetch risk factors
  useEffect(() => {
    const fetchRiskFactors = async () => {
      try {
        const response = await axios.get(`${API}/royalcert/risk-factors`, { withCredentials: true });
        setRiskFactors(response.data.risk_factors || []);
      } catch (error) {
        console.error("Risk faktörleri yüklenemedi:", error);
      } finally {
        setLoadingFactors(false);
      }
    };
    fetchRiskFactors();
  }, []);

  // Fetch customers for dropdown - only Stage 3 customers
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      // Filter to show only Stage 3 customers (Adam gün hesabı sadece 3. aşama için)
      const stage3Customers = response.data.filter(c => 
        c.audit_phase === "3" ||
        c.audit_phase === 3 ||
        c.audit_phase === "stage3" || 
        c.audit_phase === "initial_stage3" ||
        c.audit_phase === "surveillance_stage3" ||
        c.audit_phase === "recertification_stage3"
      );
      setCustomers(stage3Customers);
    } catch (error) {
      console.error("Müşteriler yüklenemedi:", error);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Fetch auditors (users with role=auditor)
  const fetchAuditors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/role/auditor`, { withCredentials: true });
      setAuditorsList(response.data || []);
    } catch (error) {
      console.error("Denetçiler yüklenemedi:", error);
    }
  }, []);

  useEffect(() => {
    fetchAuditors();
  }, [fetchAuditors]);

  // Fetch saved calculations for selected customer
  const fetchSavedCalculations = useCallback(async (customerId = null) => {
    setLoadingSavedCalcs(true);
    try {
      const url = customerId 
        ? `${API}/royalcert/manday-calculations?customer_id=${customerId}`
        : `${API}/royalcert/manday-calculations`;
      const response = await axios.get(url, { withCredentials: true });
      setSavedCalculations(response.data.calculations || []);
    } catch (error) {
      console.error("Kaydedilmiş hesaplamalar yüklenemedi:", error);
    } finally {
      setLoadingSavedCalcs(false);
    }
  }, []);

  // Load saved calculations when customer changes
  useEffect(() => {
    if (selectedCustomerId) {
      fetchSavedCalculations(selectedCustomerId);
    }
  }, [selectedCustomerId, fetchSavedCalculations]);

  // Auto-fill room count when customer selected
  useEffect(() => {
    const fillCustomerData = async () => {
      if (selectedCustomerId) {
        const customer = customers.find(c => c.customer_id === selectedCustomerId);
        if (customer) {
          if (customer.room_count) setRoomCount(customer.room_count.toString());
          if (customer.employee_count) setEmployeeCount(customer.employee_count.toString());
          if (customer.risk_level) setRiskLevel(customer.risk_level);
          // Fill FR.02 fields
          if (customer.name) setCommercialName(customer.name);
          if (customer.brand_name) setBrandName(customer.brand_name);
          if (customer.address) setHeadOfficeAddress(customer.address);
          
          // Try to get planned audit info for this customer
          try {
            const auditRes = await axios.get(`${API}/audits?customer_id=${selectedCustomerId}`, { withCredentials: true });
            const plannedAudit = auditRes.data.find(a => a.status === 'scheduled' || a.status === 'in_progress');
            if (plannedAudit) {
              // Fill audit team info
              if (plannedAudit.assigned_auditor_name) setTeamLeader(plannedAudit.assigned_auditor_name);
              if (plannedAudit.scheduled_date) setInitialCertDate(utcToTurkeyDate(plannedAudit.scheduled_date));
              
              // Get more audit details
              try {
                const detailRes = await axios.get(`${API}/audits/${plannedAudit.audit_id}`, { withCredentials: true });
                if (detailRes.data) {
                  const audit = detailRes.data;
                  if (audit.audit_team) {
                    if (audit.audit_team.team_leader) setTeamLeader(audit.audit_team.team_leader);
                    if (audit.audit_team.auditor_1) setAuditors(audit.audit_team.auditor_1);
                    if (audit.audit_team.technical_expert) setTechnicalExpert(audit.audit_team.technical_expert);
                    if (audit.audit_team.observer) setObserver(audit.audit_team.observer);
                  }
                }
              } catch (e) {
                console.log("Could not fetch audit details");
              }
            }
          } catch (e) {
            console.log("No planned audits found for customer");
          }
        }
      }
    };
    fillCustomerData();
  }, [selectedCustomerId, customers]);

  const toggleFactor = (factorId) => {
    setSelectedFactors(prev => 
      prev.includes(factorId) 
        ? prev.filter(id => id !== factorId)
        : [...prev, factorId]
    );
  };

  const calculateManDay = async () => {
    // Müşteri seçimi zorunlu
    if (!selectedCustomerId) {
      toast.error("Lütfen bir müşteri seçin");
      return;
    }
    
    if (!roomCount || roomCount <= 0) {
      toast.error("Geçerli bir oda sayısı girin");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/royalcert/calculate-manday`,
        {
          room_count: parseInt(roomCount),
          risk_level: riskLevel,
          selected_risk_factors: selectedFactors
        },
        { withCredentials: true }
      );
      
      setCalculationResult(response.data);
      
      // Add to history
      const historyItem = {
        ...response.data,
        timestamp: new Date().toISOString(),
        customer_name: customers.find(c => c.customer_id === selectedCustomerId)?.name || "Müşteri"
      };
      setCalculationHistory(prev => [historyItem, ...prev.slice(0, 9)]);
      
      toast.success("Adam-gün hesaplaması tamamlandı");
    } catch (error) {
      console.error("Hesaplama hatası:", error);
      toast.error("Hesaplama yapılamadı");
    } finally {
      setLoading(false);
    }
  };

  const saveToCustomer = async () => {
    if (!selectedCustomerId || !calculationResult) {
      toast.error("Lütfen bir müşteri seçin ve hesaplama yapın");
      return;
    }

    setSaving(true);
    try {
      await axios.patch(
        `${API}/customers/${selectedCustomerId}/manday`,
        {
          manday: calculationResult.final_manday || calculationResult.base_manday,
          risk_level: riskLevel,
          room_count: parseInt(roomCount)
        },
        { withCredentials: true }
      );
      
      toast.success("Adam-gün müşteriye kaydedildi");
      fetchCustomers(); // Refresh customer data
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      toast.error("Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  // Save calculation to planned audit
  const saveToAudit = async () => {
    if (!selectedCustomerId || !calculationResult) {
      toast.error("Lütfen bir müşteri seçin ve hesaplama yapın");
      return;
    }

    setSaving(true);
    try {
      // Find planned audit for this customer
      const auditRes = await axios.get(`${API}/audits?customer_id=${selectedCustomerId}`, { withCredentials: true });
      const plannedAudit = auditRes.data.find(a => a.status === 'scheduled' || a.status === 'in_progress');
      
      if (!plannedAudit) {
        toast.error("Bu müşteri için planlı denetim bulunamadı");
        return;
      }

      // Update audit with manday calculation
      await axios.put(
        `${API}/audits/${plannedAudit.audit_id}`,
        {
          ...plannedAudit,
          final_duration_days: calculationResult.final_manday || calculationResult.base_manday,
          audit_duration_days: calculationResult.base_manday,
          risk_level: riskLevel,
          audit_team: {
            team_leader: teamLeader,
            auditor_1: auditors,
            technical_expert: technicalExpert,
            observer: observer
          }
        },
        { withCredentials: true }
      );
      
      toast.success("Hesaplama denetim planına kaydedildi");
    } catch (error) {
      console.error("Denetim güncelleme hatası:", error);
      toast.error("Denetim planına kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  // Save calculation to database
  const saveCalculationToDb = async () => {
    if (!calculationResult) {
      toast.error("Önce hesaplama yapın");
      return;
    }

    setSavingCalculation(true);
    try {
      const customerName = selectedCustomerId 
        ? customers.find(c => c.customer_id === selectedCustomerId)?.name 
        : "Manuel Hesaplama";

      const calculationData = {
        customer_id: selectedCustomerId || null,
        customer_name: customerName,
        room_count: parseInt(roomCount) || 0,
        employee_count: parseInt(employeeCount) || 0,
        risk_level: riskLevel,
        selected_risk_factors: selectedFactors,
        base_manday: calculationResult.base_manday,
        risk_increment: calculationResult.risk_increment || 0,
        final_manday: calculationResult.final_manday || calculationResult.base_manday,
        initial_duration: calculationResult.initial,
        surveillance_duration: calculationResult.surveillance,
        recert_duration: calculationResult.recertification,
        // FR.02 fields
        pre_questions: preQuestions,
        site_type: siteType,
        shift_count: shiftCount,
        same_activity_all_shifts: sameActivityAllShifts,
        same_activity_all_sites: sameActivityAllSites,
        audit_language: auditLanguage,
        document_language: documentLanguage,
        gstc_standard_scope: gstcStandardScope,
        legal_context: legalContext,
        audit_type: auditType,
        extremely_low_risk: extremelyLowRiskFactors,
        // Team
        team_leader: teamLeader,
        auditors: auditors,
        technical_expert: technicalExpert,
        observer: observer,
        // Dates
        initial_cert_date: initialCertDate,
        surveillance_1_date: surveillance1Date,
        surveillance_2_date: surveillance2Date,
        recert_date: recertDate,
        // Evaluation
        evaluation: evaluation,
        // Approval & Review - use the field names backend expects
        approved_name: approvedBy,
        approved_date: approvedDate,
        reviewed_name: reviewedBy,
        reviewed_date: reviewedDate,
        notes: calculationNotes
      };

      if (currentCalculationId) {
        // Update existing
        await axios.put(
          `${API}/royalcert/manday-calculations/${currentCalculationId}`,
          calculationData,
          { withCredentials: true }
        );
        toast.success("Hesaplama güncellendi");
      } else {
        // Create new
        const response = await axios.post(
          `${API}/royalcert/manday-calculations`,
          calculationData,
          { withCredentials: true }
        );
        setCurrentCalculationId(response.data.calculation_id);
        toast.success("Hesaplama kaydedildi");
      }

      // Refresh saved calculations list
      if (selectedCustomerId) {
        fetchSavedCalculations(selectedCustomerId);
      }
    } catch (error) {
      console.error("Hesaplama kaydetme hatası:", error);
      toast.error("Hesaplama kaydedilemedi");
    } finally {
      setSavingCalculation(false);
    }
  };

  // Load a saved calculation into the form
  const loadSavedCalculation = (calc) => {
    // Set customer first
    if (calc.customer_id) {
      setSelectedCustomerId(calc.customer_id);
    }
    
    // Basic info
    setRoomCount(calc.room_count?.toString() || "");
    setEmployeeCount(calc.employee_count?.toString() || "");
    setRiskLevel(calc.risk_level || "low_risk");
    setSelectedFactors(calc.selected_risk_factors || []);
    
    // Pre-questions
    if (calc.pre_questions) {
      setPreQuestions(calc.pre_questions);
    }
    
    // FR.02 fields
    setSiteType(calc.site_type || "single");
    setShiftCount(calc.shift_count || "1");
    setSameActivityAllShifts(calc.same_activity_all_shifts ?? true);
    setSameActivityAllSites(calc.same_activity_all_sites ?? true);
    setAuditLanguage(calc.audit_language || "Turkish");
    setDocumentLanguage(calc.document_language || "English");
    setGstcStandardScope(calc.gstc_standard_scope || "GSTC Standard for Hotels & Accommodations V3.0");
    setLegalContext(calc.legal_context || "");
    setAuditType(calc.audit_type || "initial");
    
    // Extremely low risk
    if (calc.extremely_low_risk) {
      setExtremelyLowRiskFactors(calc.extremely_low_risk);
    }
    
    // Team
    setTeamLeader(calc.team_leader || "");
    setAuditors(calc.auditors || "");
    setTechnicalExpert(calc.technical_expert || "");
    setObserver(calc.observer || "");
    
    // Dates
    setInitialCertDate(calc.initial_cert_date || "");
    setSurveillance1Date(calc.surveillance_1_date || "");
    setSurveillance2Date(calc.surveillance_2_date || "");
    setRecertDate(calc.recert_date || "");
    
    // Evaluation
    if (calc.evaluation) {
      setEvaluation(calc.evaluation);
    }
    
    // Approval & Review
    setApprovedBy(calc.approved_by || calc.approved_name || "");
    setApprovedDate(calc.approved_date || "");
    setReviewedBy(calc.reviewed_by || calc.reviewed_name || "");
    setReviewedDate(calc.reviewed_date || "");
    
    // Notes
    setCalculationNotes(calc.notes || "");
    
    // Set calculation result
    setCalculationResult({
      room_count: calc.room_count,
      room_category: calc.room_count <= 40 ? "1-40" : calc.room_count <= 100 ? "41-100" : calc.room_count <= 200 ? "101-200" : calc.room_count <= 500 ? "201-500" : calc.room_count <= 999 ? "501-999" : "1000+",
      risk_level: calc.risk_level,
      base_manday: calc.base_manday,
      risk_increment: calc.risk_increment,
      final_manday: calc.final_manday,
      initial: calc.initial_duration,
      surveillance: calc.surveillance_duration,
      recertification: calc.recert_duration
    });
    
    // Set current calculation ID for updates
    setCurrentCalculationId(calc.calculation_id);
    
    // Close dialog
    setShowSavedCalcsDialog(false);
    
    toast.success("Hesaplama yüklendi");
  };

  // Delete a saved calculation
  const deleteSavedCalculation = async (calculationId) => {
    if (!window.confirm("Bu hesaplamayı silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await axios.delete(
        `${API}/royalcert/manday-calculations/${calculationId}`,
        { withCredentials: true }
      );
      toast.success("Hesaplama silindi");
      
      // Clear current if deleted
      if (currentCalculationId === calculationId) {
        setCurrentCalculationId(null);
      }
      
      // Refresh list
      if (selectedCustomerId) {
        fetchSavedCalculations(selectedCustomerId);
      }
    } catch (error) {
      console.error("Hesaplama silme hatası:", error);
      toast.error("Hesaplama silinemedi");
    }
  };

  // Reset form for new calculation
  const startNewCalculation = () => {
    setCurrentCalculationId(null);
    setCalculationResult(null);
    setRoomCount("");
    setEmployeeCount("");
    setRiskLevel("low_risk");
    setSelectedFactors([]);
    setPreQuestions({
      certificateWithdrawn: null,
      certificateSuspended: null,
      associationWithBanned: null,
      litigationOrViolation: null
    });
    setSiteType("single");
    setShiftCount("1");
    setSameActivityAllShifts(true);
    setSameActivityAllSites(true);
    setAuditLanguage("Turkish");
    setDocumentLanguage("English");
    setGstcStandardScope("GSTC Standard for Hotels & Accommodations V3.0");
    setLegalContext("");
    setAuditType("initial");
    setExtremelyLowRiskFactors({
      lessThan20Rooms: false,
      noConferenceVenue: false,
      localResident: false,
      notInSensitiveArea: false
    });
    setTeamLeader("");
    setAuditors("");
    setTechnicalExpert("");
    setObserver("");
    setInitialCertDate("");
    setSurveillance1Date("");
    setSurveillance2Date("");
    setRecertDate("");
    setEvaluation({
      relatedRegulations: "conform",
      smsConformity: "conform",
      groupManagerAppointment: "conform",
      canBeProposed: true,
      explanation: ""
    });
    setApprovedBy("");
    setApprovedDate("");
    setReviewedBy("");
    setReviewedDate("");
    setCalculationNotes("");
    toast.info("Form temizlendi - Yeni hesaplama başlatıldı");
  };

  // Download FR.02 Application Review Form
  const downloadFR02 = async () => {
    if (!calculationResult) {
      toast.error("Önce hesaplama yapın");
      return;
    }

    setDownloadingFR02(true);
    try {
      // Build FR.02 data with all fields
      const fr02Data = {
        auto_fill_data: {
          customer: {
            name: commercialName,
            brand_name: brandName,
            address: headOfficeAddress,
            room_count: parseInt(roomCount) || 0,
            employee_count: parseInt(employeeCount) || 0
          },
          audit: {
            audit_type: auditType === "initial" ? "Initial Certification" : 
                       auditType === "surveillance" ? "Surveillance" : 
                       auditType === "recertification" ? "Re-Certification" : "Initial Certification",
            scheduled_date: initialCertDate,
            duration_days: calculationResult.final_manday || calculationResult.base_manday,
            team_leader: teamLeader
          },
          planning: {
            audit_team: {
              team_leader: teamLeader,
              auditor_1: auditors,
              technical_expert: technicalExpert,
              observer: observer
            }
          }
        },
        report_data: {
          header_data: {
            // FR.02 Table 1 - Pre Questions
            pre_questions: preQuestions,
            
            // FR.02 Table 2 - Client Information
            commercial_name: commercialName,
            brand_name: brandName,
            address: headOfficeAddress,
            site_addresses: siteAddresses,
            room_count: parseInt(roomCount) || 0,
            employee_count: parseInt(employeeCount) || 0,
            shift_count: shiftCount,
            same_activity_all_shifts: sameActivityAllShifts,
            same_activity_all_sites: sameActivityAllSites,
            audit_language: auditLanguage,
            document_language: documentLanguage,
            gstc_standard_scope: gstcStandardScope,
            legal_context: legalContext,
            audit_type: auditType,
            
            // FR.02 Table 0 - Site Type
            site_type: siteType,
            
            // FR.02 Table 4 - Risk Factors
            selected_risk_factors: selectedFactors,
            
            // FR.02 Table 5 - Extremely Low Risk
            extremely_low_risk: extremelyLowRiskFactors,
            
            // FR.02 Table 7 - Duration Calculation
            base_manday: calculationResult.base_manday,
            risk_increment: calculationResult.risk_increment,
            final_manday: calculationResult.final_manday,
            initial_duration: calculationResult.initial,
            surveillance_duration: calculationResult.surveillance,
            recert_duration: calculationResult.recertification,
            
            // FR.02 Table 9 - Audit Team
            team_leader: teamLeader,
            auditor_1: auditors,
            technical_expert: technicalExpert,
            observer: observer,
            
            // FR.02 Table 9 - Audit Dates
            initial_cert_date: initialCertDate,
            surveillance_1_date: surveillance1Date,
            surveillance_2_date: surveillance2Date,
            recert_date: recertDate,
            audit_date: initialCertDate,
            
            // FR.02 Table 9 - Evaluation
            evaluation: evaluation,
            risk_level: riskLevel,
            
            // FR.02 Table 9 - Approval & Review
            approved_name: approvedBy,
            approved_date: approvedDate,
            reviewed_name: reviewedBy,
            reviewed_date: reviewedDate
          }
        },
        // Include risk factors with their selection status
        risk_factors_selection: riskFactors.map(f => ({
          ...f,
          selected: selectedFactors.includes(f.id)
        }))
      };

      const response = await axios.post(
        `${API}/royalcert/generate-fr02`,
        fr02Data,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FR.02_${commercialName || 'Application_Review'}_${new Date().toISOString().split('T')[0]}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("FR.02 formu indirildi");
      setShowFR02Dialog(false);
    } catch (error) {
      console.error("FR.02 indirme hatası:", error);
      toast.error("FR.02 indirilemedi");
    } finally {
      setDownloadingFR02(false);
    }
  };

  // Download FR.02 as PDF
  const [downloadingFR02PDF, setDownloadingFR02PDF] = useState(false);
  
  const downloadFR02PDF = async () => {
    if (!calculationResult) {
      toast.error("Önce hesaplama yapın");
      return;
    }

    setDownloadingFR02PDF(true);
    try {
      // Build same data as DOCX download
      const fr02Data = {
        auto_fill_data: {
          customer: {
            name: commercialName,
            brand_name: brandName,
            address: headOfficeAddress,
            room_count: parseInt(roomCount) || 0,
            employee_count: parseInt(employeeCount) || 0
          },
          audit: {
            audit_type: auditType === "initial" ? "Initial Certification" : 
                       auditType === "surveillance" ? "Surveillance" : 
                       auditType === "recertification" ? "Re-Certification" : "Initial Certification",
            scheduled_date: initialCertDate,
            duration_days: calculationResult.final_manday || calculationResult.base_manday,
            team_leader: teamLeader
          },
          planning: {
            audit_team: {
              team_leader: teamLeader,
              auditor_1: auditors,
              technical_expert: technicalExpert,
              observer: observer
            }
          }
        },
        report_data: {
          header_data: {
            pre_questions: preQuestions,
            commercial_name: commercialName,
            brand_name: brandName,
            address: headOfficeAddress,
            site_addresses: siteAddresses,
            room_count: parseInt(roomCount) || 0,
            employee_count: parseInt(employeeCount) || 0,
            shift_count: shiftCount,
            same_activity_all_shifts: sameActivityAllShifts,
            same_activity_all_sites: sameActivityAllSites,
            audit_language: auditLanguage,
            document_language: documentLanguage,
            gstc_standard_scope: gstcStandardScope,
            legal_context: legalContext,
            audit_type: auditType,
            site_type: siteType,
            selected_risk_factors: selectedFactors,
            extremely_low_risk: extremelyLowRiskFactors,
            base_manday: calculationResult.base_manday,
            risk_increment: calculationResult.risk_increment,
            final_manday: calculationResult.final_manday,
            initial_duration: calculationResult.initial,
            surveillance_duration: calculationResult.surveillance,
            recert_duration: calculationResult.recertification,
            team_leader: teamLeader,
            auditor_1: auditors,
            technical_expert: technicalExpert,
            observer: observer,
            initial_cert_date: initialCertDate,
            surveillance_1_date: surveillance1Date,
            surveillance_2_date: surveillance2Date,
            recert_date: recertDate,
            audit_date: initialCertDate,
            evaluation: evaluation,
            risk_level: riskLevel,
            approved_name: approvedBy,
            approved_date: approvedDate,
            reviewed_name: reviewedBy,
            reviewed_date: reviewedDate
          }
        }
      };

      const response = await axios.post(
        `${API}/royalcert/generate-fr02-pdf`,
        fr02Data,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FR.02_${commercialName || 'Application_Review'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("FR.02 PDF indirildi");
    } catch (error) {
      console.error("FR.02 PDF indirme hatası:", error);
      toast.error("FR.02 PDF indirilemedi");
    } finally {
      setDownloadingFR02PDF(false);
    }
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      "high_risk": "Yüksek Risk",
      "low_risk": "Düşük Risk",
      "extremely_low_risk": "Çok Düşük Risk"
    };
    return labels[level] || level;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      "high_risk": "bg-red-100 text-red-800 border-red-300",
      "low_risk": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "extremely_low_risk": "bg-green-100 text-green-800 border-green-300"
    };
    return colors[level] || "bg-gray-100 text-gray-800";
  };

  // Only show for RoyalCert tenant - redirect others to dashboard
  if (tenantSlug !== 'royalcert') {
    window.location.href = '/dashboard';
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manday-calculator-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
            <Calculator className="w-7 h-7 text-purple-600" />
            Adam-Gün Hesaplama (TL.01)
            {currentCalculationId && (
              <Badge variant="outline" className="ml-2 text-xs bg-green-50 text-green-700 border-green-300">
                <Edit className="w-3 h-3 mr-1" />
                Düzenleniyor
              </Badge>
            )}
          </h1>
          <p className="text-[#4B5563]">GSTC denetim süresi hesaplayıcı - FR.02 kriterleri</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={startNewCalculation}
            className="border-green-500 text-green-600 hover:bg-green-50"
            data-testid="new-calculation-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Yeni Hesaplama
          </Button>
          <Dialog open={showSavedCalcsDialog} onOpenChange={setShowSavedCalcsDialog}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchSavedCalculations(selectedCustomerId || null);
                  setShowSavedCalcsDialog(true);
                }}
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="saved-calculations-btn"
              >
                <History className="w-4 h-4 mr-1" />
                Kaydedilmiş ({savedCalculations.length})
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-purple-600" />
                  Kaydedilmiş Hesaplamalar
                </DialogTitle>
                <DialogDescription>
                  {selectedCustomerId 
                    ? `${customers.find(c => c.customer_id === selectedCustomerId)?.name || 'Müşteri'} için kaydedilmiş hesaplamalar`
                    : 'Tüm kaydedilmiş hesaplamalar'}
                </DialogDescription>
              </DialogHeader>
              
              {loadingSavedCalcs ? (
                <div className="flex items-center justify-center py-8">
                  <Clock className="w-6 h-6 animate-spin text-purple-600" />
                  <span className="ml-2 text-gray-500">Yükleniyor...</span>
                </div>
              ) : savedCalculations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Henüz kaydedilmiş hesaplama yok</p>
                  <p className="text-sm">Hesaplama yapın ve "Hesaplamayı Kaydet" butonuna tıklayın</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedCalculations.map((calc) => (
                    <div 
                      key={calc.calculation_id} 
                      className={`p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                        currentCalculationId === calc.calculation_id ? 'border-purple-400 bg-purple-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{calc.customer_name || 'İsimsiz Hesaplama'}</h4>
                            <Badge className={getRiskLevelColor(calc.risk_level)}>
                              {getRiskLevelLabel(calc.risk_level)}
                            </Badge>
                            {currentCalculationId === calc.calculation_id && (
                              <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                                Aktif
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-gray-500 grid grid-cols-2 gap-x-4">
                            <span>Oda: {calc.room_count}</span>
                            <span>Personel: {calc.employee_count || '-'}</span>
                            <span>Temel A/G: {calc.base_manday}</span>
                            <span className="font-medium text-purple-700">Toplam A/G: {calc.final_manday}</span>
                          </div>
                          {calc.notes && (
                            <p className="mt-2 text-xs text-gray-400 italic">Not: {calc.notes}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Oluşturulma: {new Date(calc.created_at).toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => loadSavedCalculation(calc)}
                            className="text-blue-600 hover:bg-blue-50"
                            data-testid={`load-calc-${calc.calculation_id}`}
                          >
                            <FolderOpen className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteSavedCalculation(calc.calculation_id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-calc-${calc.calculation_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calculation Form */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* FR.02 Pre-Questions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Ön Değerlendirme Soruları
              </CardTitle>
              <CardDescription>FR.02 Tablo 1 - Başvuru değerlendirmesi için yanıtlanması gereken sorular</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {[
                  { key: "certificateWithdrawn", label: "Başvuranın GSTC sertifikası iptal edildi mi? / Has the applicant's GSTC certificate been withdrawn?" },
                  { key: "certificateSuspended", label: "Başvuranın GSTC sertifikası askıya alındı mı? / Has the applicant's GSTC certificate been suspended?" },
                  { key: "associationWithBanned", label: "Başvuranın belgelendirme yasaklı kuruluşlarla ilişkisi var mı? / Does the applicant have any association with organizations banned from certification?" },
                  { key: "litigationOrViolation", label: "Başvuranın sürdürülebilirlik davası/ihlali var mı? / Does the applicant have any sustainability litigation or violation?" }
                ].map((q) => (
                  <div key={q.key} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border">
                    <span className="text-sm flex-1 pr-4">{q.label}</span>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={q.key}
                          checked={preQuestions[q.key] === true}
                          onChange={() => setPreQuestions(prev => ({ ...prev, [q.key]: true }))}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Evet</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={q.key}
                          checked={preQuestions[q.key] === false}
                          onChange={() => setPreQuestions(prev => ({ ...prev, [q.key]: false }))}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Hayır</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Customer Selection & Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Tesis Bilgileri
              </CardTitle>
              <CardDescription>Hesaplama için müşteri seçimi zorunludur</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Müşteri <span className="text-red-500">*</span>
                  </Label>
                  <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                    <SelectTrigger data-testid="customer-select" className={!selectedCustomerId ? "border-red-300" : ""}>
                      <SelectValue placeholder="Müşteri seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          {c.name} {c.room_count ? `(${c.room_count} oda)` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!selectedCustomerId && (
                    <p className="text-xs text-red-500">Müşteri seçimi zorunludur</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roomCount" className="flex items-center gap-2">
                    <Hotel className="w-4 h-4" />
                    Oda Sayısı *
                  </Label>
                  <Input
                    id="roomCount"
                    type="number"
                    min="1"
                    value={roomCount}
                    onChange={(e) => setRoomCount(e.target.value)}
                    placeholder="Örn: 150"
                    data-testid="room-count-input"
                  />
                </div>
              </div>
              
              {/* Additional FR.02 Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Personel Sayısı</Label>
                  <Input
                    type="number"
                    min="0"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(e.target.value)}
                    placeholder="Örn: 50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vardiya Sayısı</Label>
                  <Input
                    type="number"
                    min="1"
                    max="4"
                    value={shiftCount}
                    onChange={(e) => setShiftCount(e.target.value)}
                    placeholder="Örn: 1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tesis Tipi</Label>
                  <Select value={siteType} onValueChange={setSiteType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Tek Şube (Single)</SelectItem>
                      <SelectItem value="multi">Çoklu Şube (Multi-site)</SelectItem>
                      <SelectItem value="group">Grup (Group)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Activity Scope Questions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Tüm vardiyalarda faaliyet kapsamları aynı mı?</span>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" checked={sameActivityAllShifts} onChange={() => setSameActivityAllShifts(true)} />
                      <span className="text-sm">Evet</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" checked={!sameActivityAllShifts} onChange={() => setSameActivityAllShifts(false)} />
                      <span className="text-sm">Hayır</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Tüm şubelerde faaliyet kapsamları aynı mı?</span>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" checked={sameActivityAllSites} onChange={() => setSameActivityAllSites(true)} />
                      <span className="text-sm">Evet</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" checked={!sameActivityAllSites} onChange={() => setSameActivityAllSites(false)} />
                      <span className="text-sm">Hayır</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Language Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label>Denetim Dili</Label>
                  <Select value={auditLanguage} onValueChange={setAuditLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Turkish">Türkçe</SelectItem>
                      <SelectItem value="English">İngilizce</SelectItem>
                      <SelectItem value="German">Almanca</SelectItem>
                      <SelectItem value="Russian">Rusça</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Döküman Dili</Label>
                  <Select value={documentLanguage} onValueChange={setDocumentLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Turkish">Türkçe</SelectItem>
                      <SelectItem value="English">İngilizce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* GSTC Standard Scope */}
              <div className="space-y-2">
                <Label>GSTC Standardı Kapsamı</Label>
                <Select value={gstcStandardScope} onValueChange={setGstcStandardScope}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GSTC Standard for Hotels & Accommodations V3.0">GSTC Standard for Hotels & Accommodations V3.0</SelectItem>
                    <SelectItem value="GSTC Standard for Tour Operators V3.0">GSTC Standard for Tour Operators V3.0</SelectItem>
                    <SelectItem value="GSTC Standard for Destinations V2.0">GSTC Standard for Destinations V2.0</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Legal Context */}
              <div className="space-y-2">
                <Label>Yasal Düzenlemeler / Legal Context</Label>
                <textarea
                  className="w-full p-2 border rounded-md text-sm"
                  rows={2}
                  value={legalContext}
                  onChange={(e) => setLegalContext(e.target.value)}
                  placeholder="Yerel, ulusal, bölgesel veya sektörel düzenlemeler..."
                />
              </div>

              <div className="space-y-2">
                <Label>Risk Seviyesi *</Label>
                <Select value={riskLevel} onValueChange={setRiskLevel}>
                  <SelectTrigger data-testid="risk-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high_risk">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Yüksek Risk
                      </span>
                    </SelectItem>
                    <SelectItem value="low_risk">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                        Düşük Risk
                      </span>
                    </SelectItem>
                    <SelectItem value="extremely_low_risk">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Çok Düşük Risk
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {riskLevel === "extremely_low_risk" && "Sadece 1-100 oda için geçerlidir"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Risk Factors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Risk Artırım Faktörleri
              </CardTitle>
              <CardDescription>
                Her seçili faktör için belirtilen miktar kadar adam-gün eklenir
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingFactors ? (
                <div className="text-center py-4">Yükleniyor...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                  {riskFactors.map((factor) => (
                    <div
                      key={factor.id}
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedFactors.includes(factor.id)
                          ? "bg-purple-50 border-purple-300"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleFactor(factor.id)}
                    >
                      <Checkbox
                        id={factor.id}
                        checked={selectedFactors.includes(factor.id)}
                        onCheckedChange={() => toggleFactor(factor.id)}
                      />
                      <div className="flex-1">
                        <label htmlFor={factor.id} className="text-sm cursor-pointer">
                          {factor.label}
                        </label>
                        <Badge variant="outline" className="ml-2 text-xs">
                          +{factor.increment} A/G
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedFactors.length > 0 && (
                <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm font-medium text-orange-800">
                    Seçili faktörler: {selectedFactors.length} adet
                  </p>
                  <p className="text-sm text-orange-600">
                    Toplam artırım: +{riskFactors
                      .filter(f => selectedFactors.includes(f.id))
                      .reduce((sum, f) => sum + f.increment, 0)
                      .toFixed(2)} A/G
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Extremely Low Risk Factors */}
          {riskLevel === "extremely_low_risk" && (
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  Çok Düşük Risk Kriterleri (FR.02)
                </CardTitle>
                <CardDescription>
                  Çok düşük risk için tüm kriterlerin karşılanması gerekir
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-green-50">
                  <Checkbox
                    id="lessThan20Rooms"
                    checked={extremelyLowRiskFactors.lessThan20Rooms}
                    onCheckedChange={(checked) => setExtremelyLowRiskFactors(prev => ({...prev, lessThan20Rooms: checked}))}
                  />
                  <label htmlFor="lessThan20Rooms" className="text-sm cursor-pointer">
                    20'den az misafir odası ve 15'ten az çalışan
                  </label>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-green-50">
                  <Checkbox
                    id="noConferenceVenue"
                    checked={extremelyLowRiskFactors.noConferenceVenue}
                    onCheckedChange={(checked) => setExtremelyLowRiskFactors(prev => ({...prev, noConferenceVenue: checked}))}
                  />
                  <label htmlFor="noConferenceVenue" className="text-sm cursor-pointer">
                    Konferans, düğün vb. etkinlik alanı bulunmuyor
                  </label>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-green-50">
                  <Checkbox
                    id="localResident"
                    checked={extremelyLowRiskFactors.localResident}
                    onCheckedChange={(checked) => setExtremelyLowRiskFactors(prev => ({...prev, localResident: checked}))}
                  />
                  <label htmlFor="localResident" className="text-sm cursor-pointer">
                    Tesis yerel halk tarafından işletiliyor (yabancı şirket hariç)
                  </label>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border bg-green-50">
                  <Checkbox
                    id="notInSensitiveArea"
                    checked={extremelyLowRiskFactors.notInSensitiveArea}
                    onCheckedChange={(checked) => setExtremelyLowRiskFactors(prev => ({...prev, notInSensitiveArea: checked}))}
                  />
                  <label htmlFor="notInSensitiveArea" className="text-sm cursor-pointer">
                    Hassas bölgelerde bulunmuyor
                  </label>
                </div>
                
                {Object.values(extremelyLowRiskFactors).every(v => v) ? (
                  <div className="p-3 bg-green-100 rounded-lg border border-green-300 text-green-800 text-sm">
                    ✓ Tüm kriterler karşılanıyor - Çok düşük risk uygulanabilir
                  </div>
                ) : (
                  <div className="p-3 bg-yellow-100 rounded-lg border border-yellow-300 text-yellow-800 text-sm">
                    ⚠ Tüm kriterler karşılanmalı
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* FR.02 Additional Info - Shown after calculation */}
          {calculationResult && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <FileText className="w-5 h-5" />
                  FR.02 Form Bilgileri
                </CardTitle>
                <CardDescription>
                  Başvuru İnceleme Formu için ek bilgiler
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tesis Türü</Label>
                    <Select value={siteType} onValueChange={setSiteType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Tek Saha (Single Site)</SelectItem>
                        <SelectItem value="multi">Çoklu Saha (Multi-Site)</SelectItem>
                        <SelectItem value="group">Grup Oteller</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Denetim Dili</Label>
                    <Select value={auditLanguage} onValueChange={setAuditLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Turkish">Türkçe</SelectItem>
                        <SelectItem value="English">İngilizce</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ticari Unvan</Label>
                    <Input 
                      value={commercialName} 
                      onChange={(e) => setCommercialName(e.target.value)}
                      placeholder="Şirket adı"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Marka Adı</Label>
                    <Input 
                      value={brandName} 
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="Otel/tesis markası"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Merkez Adres</Label>
                  <Textarea 
                    value={headOfficeAddress} 
                    onChange={(e) => setHeadOfficeAddress(e.target.value)}
                    placeholder="Ana tesis adresi"
                    rows={2}
                  />
                </div>
                
                {siteType !== "single" && (
                  <div className="space-y-2">
                    <Label>Şube Adresleri</Label>
                    <Textarea 
                      value={siteAddresses} 
                      onChange={(e) => setSiteAddresses(e.target.value)}
                      placeholder="Diğer şube adresleri (her biri ayrı satırda)"
                      rows={3}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Çalışan Sayısı</Label>
                  <Input 
                    type="number"
                    value={employeeCount} 
                    onChange={(e) => setEmployeeCount(e.target.value)}
                    placeholder="Toplam çalışan sayısı"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Yasal Bağlam (Opsiyonel)</Label>
                  <Textarea 
                    value={legalContext} 
                    onChange={(e) => setLegalContext(e.target.value)}
                    placeholder="Yerel, ulusal ve uluslararası yasal düzenlemeler..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audit Team & Dates - After FR.02 Info */}
          {calculationResult && (
            <Card className="border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Users className="w-5 h-5" />
                  Denetim Ekibi ve Tarihler
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Ekip Lideri */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Ekip Lideri</Label>
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={manualTeamLeader}
                          onChange={(e) => setManualTeamLeader(e.target.checked)}
                          className="w-3 h-3"
                        />
                        Elle gir
                      </label>
                    </div>
                    {manualTeamLeader ? (
                      <Input 
                        value={teamLeader} 
                        onChange={(e) => setTeamLeader(e.target.value)}
                        placeholder="Baş denetçi adı"
                      />
                    ) : (
                      <Select value={teamLeader} onValueChange={setTeamLeader}>
                        <SelectTrigger>
                          <SelectValue placeholder="Denetçi seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {auditorsList.map(a => (
                            <SelectItem key={a.user_id} value={a.name}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Denetçi */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Denetçi(ler)</Label>
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={manualAuditor}
                          onChange={(e) => setManualAuditor(e.target.checked)}
                          className="w-3 h-3"
                        />
                        Elle gir
                      </label>
                    </div>
                    {manualAuditor ? (
                      <Input 
                        value={auditors} 
                        onChange={(e) => setAuditors(e.target.value)}
                        placeholder="Denetçi adları"
                      />
                    ) : (
                      <Select value={auditors} onValueChange={setAuditors}>
                        <SelectTrigger>
                          <SelectValue placeholder="Denetçi seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {auditorsList.map(a => (
                            <SelectItem key={a.user_id} value={a.name}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Teknik Uzman */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Teknik Uzman</Label>
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={manualTechExpert}
                          onChange={(e) => setManualTechExpert(e.target.checked)}
                          className="w-3 h-3"
                        />
                        Elle gir
                      </label>
                    </div>
                    {manualTechExpert ? (
                      <Input 
                        value={technicalExpert} 
                        onChange={(e) => setTechnicalExpert(e.target.value)}
                        placeholder="Varsa teknik uzman"
                      />
                    ) : (
                      <Select value={technicalExpert || "_none_"} onValueChange={(val) => setTechnicalExpert(val === "_none_" ? "" : val)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Uzman seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">-- Yok --</SelectItem>
                          {auditorsList.map(a => (
                            <SelectItem key={a.user_id} value={a.name}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Gözlemci / Aday */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Gözlemci / Aday</Label>
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={manualObserver}
                          onChange={(e) => setManualObserver(e.target.checked)}
                          className="w-3 h-3"
                        />
                        Elle gir
                      </label>
                    </div>
                    {manualObserver ? (
                      <Input 
                        value={observer} 
                        onChange={(e) => setObserver(e.target.value)}
                        placeholder="Gözlemci veya aday denetçi"
                      />
                    ) : (
                      <Select value={observer || "_none_"} onValueChange={(val) => setObserver(val === "_none_" ? "" : val)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Gözlemci seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">-- Yok --</SelectItem>
                          {auditorsList.map(a => (
                            <SelectItem key={a.user_id} value={a.name}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium mb-3 block">Denetim Tarihleri</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">İlk Belgelendirme</Label>
                      <Input 
                        type="date"
                        value={initialCertDate} 
                        onChange={(e) => setInitialCertDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">1. Gözetim</Label>
                      <Input 
                        type="date"
                        value={surveillance1Date} 
                        onChange={(e) => setSurveillance1Date(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">2. Gözetim</Label>
                      <Input 
                        type="date"
                        value={surveillance2Date} 
                        onChange={(e) => setSurveillance2Date(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Yeniden Belgelendirme</Label>
                      <Input 
                        type="date"
                        value={recertDate} 
                        onChange={(e) => setRecertDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* FR.02 Evaluation - Table 9 */}
          {calculationResult && (
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-5 h-5" />
                  Değerlendirme (FR.02 Tablo 9)
                </CardTitle>
                <CardDescription>
                  Başvuru inceleme değerlendirmesi
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>İlgili Düzenlemelere Uygunluk</Label>
                    <Select 
                      value={evaluation.relatedRegulations} 
                      onValueChange={(val) => setEvaluation(prev => ({...prev, relatedRegulations: val}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conform">Uygun (Conform)</SelectItem>
                        <SelectItem value="non-conform">Uygun Değil (Non-conform)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>SMS Uygunluğu</Label>
                    <Select 
                      value={evaluation.smsConformity} 
                      onValueChange={(val) => setEvaluation(prev => ({...prev, smsConformity: val}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conform">Uygun (Conform)</SelectItem>
                        <SelectItem value="non-conform">Uygun Değil (Non-conform)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {siteType === "group" && (
                  <div className="space-y-2">
                    <Label>Grup Yöneticisi Ataması</Label>
                    <Select 
                      value={evaluation.groupManagerAppointment} 
                      onValueChange={(val) => setEvaluation(prev => ({...prev, groupManagerAppointment: val}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conform">Uygun (Conform)</SelectItem>
                        <SelectItem value="non-conform">Uygun Değil (Non-conform)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">Belgelendirme için önerilebilir mi?</span>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={evaluation.canBeProposed}
                        onChange={() => setEvaluation(prev => ({...prev, canBeProposed: true}))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-green-600 font-medium">Evet</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!evaluation.canBeProposed}
                        onChange={() => setEvaluation(prev => ({...prev, canBeProposed: false}))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-red-600 font-medium">Hayır</span>
                    </label>
                  </div>
                </div>
                
                {!evaluation.canBeProposed && (
                  <div className="space-y-2">
                    <Label>Açıklama (Önerilmeme nedeni)</Label>
                    <textarea
                      className="w-full p-2 border rounded-md text-sm"
                      rows={2}
                      value={evaluation.explanation}
                      onChange={(e) => setEvaluation(prev => ({...prev, explanation: e.target.value}))}
                      placeholder="Belgelendirme için önerilmeme gerekçesi..."
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Approval & Review Section */}
          {calculationResult && (
            <Card className="border-indigo-200 bg-indigo-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-indigo-700 text-base">
                  <Users className="w-5 h-5" />
                  Onay ve Gözden Geçirme
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Approved By */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Onaylayan (Approved by)</Label>
                    <Input 
                      value={approvedBy} 
                      onChange={(e) => setApprovedBy(e.target.value)}
                      placeholder="Ad Soyad"
                      className="text-sm"
                    />
                    <Input 
                      type="date"
                      value={approvedDate} 
                      onChange={(e) => setApprovedDate(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  
                  {/* Reviewed By */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Gözden Geçiren (Reviewed by)</Label>
                    <Input 
                      value={reviewedBy} 
                      onChange={(e) => setReviewedBy(e.target.value)}
                      placeholder="Ad Soyad"
                      className="text-sm"
                    />
                    <Input 
                      type="date"
                      value={reviewedDate} 
                      onChange={(e) => setReviewedDate(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes & Save Section - Shows after calculation */}
          {calculationResult && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-amber-700 text-base">
                  <Save className="w-5 h-5" />
                  Hesaplamayı Kaydet
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">Not (Opsiyonel)</Label>
                  <Textarea 
                    value={calculationNotes} 
                    onChange={(e) => setCalculationNotes(e.target.value)}
                    placeholder="Bu hesaplama hakkında notlar..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <Button
                  onClick={saveCalculationToDb}
                  disabled={savingCalculation}
                  className={`w-full ${currentCalculationId 
                    ? 'bg-amber-600 hover:bg-amber-700' 
                    : 'bg-green-600 hover:bg-green-700'}`}
                  data-testid="save-calculation-btn"
                >
                  {savingCalculation ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : currentCalculationId ? (
                    <>
                      <Edit className="w-4 h-4 mr-2" />
                      Hesaplamayı Güncelle
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Hesaplamayı Kaydet
                    </>
                  )}
                </Button>
                {currentCalculationId && (
                  <p className="text-xs text-center text-amber-600">
                    ID: {currentCalculationId}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Calculate Button */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={calculateManDay}
              disabled={loading || !roomCount}
              className="bg-purple-600 hover:bg-purple-700 flex-1"
              data-testid="calculate-btn"
            >
              {loading ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Hesaplanıyor...
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" />
                  Hesapla
                </>
              )}
            </Button>
            
            {calculationResult && selectedCustomerId && (
              <Button
                onClick={saveToCustomer}
                disabled={saving}
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                data-testid="save-to-customer-btn"
              >
                {saving ? (
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Müşteriye Kaydet
              </Button>
            )}
            
            {calculationResult && selectedCustomerId && (
              <Button
                onClick={saveToAudit}
                disabled={saving}
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="save-to-audit-btn"
              >
                {saving ? (
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CalendarCheck className="w-4 h-4 mr-2" />
                )}
                Denetim Planına Kaydet
              </Button>
            )}
            
            {calculationResult && (
              <div className="flex gap-2">
                <Button
                  onClick={downloadFR02}
                  disabled={downloadingFR02}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  data-testid="download-fr02-btn"
                >
                  {downloadingFR02 ? (
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  FR.02 Word
                </Button>
                <Button
                  onClick={downloadFR02PDF}
                  disabled={downloadingFR02PDF}
                  variant="outline"
                  className="border-red-500 text-red-600 hover:bg-red-50"
                  data-testid="download-fr02-pdf-btn"
                >
                  {downloadingFR02PDF ? (
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  FR.02 PDF
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Results & Reference */}
        <div className="space-y-6">
          {/* Calculation Result */}
          {calculationResult && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <CheckCircle className="w-5 h-5" />
                  Hesaplama Sonucu
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-purple-600">
                    {calculationResult.final_manday || calculationResult.base_manday} A/G
                  </p>
                  <p className="text-sm text-purple-500 mt-1">Toplam Denetim Süresi</p>
                </div>
                
                <div className="space-y-2 pt-4 border-t border-purple-200">
                  <div className="flex justify-between text-sm">
                    <span>Oda Kategorisi:</span>
                    <span className="font-medium">{calculationResult.room_category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Risk Seviyesi:</span>
                    <Badge className={getRiskLevelColor(calculationResult.risk_level)}>
                      {getRiskLevelLabel(calculationResult.risk_level)}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Temel Adam-Gün:</span>
                    <span className="font-medium">{calculationResult.base_manday} A/G</span>
                  </div>
                  {calculationResult.risk_increment > 0 && (
                    <div className="flex justify-between text-sm text-orange-600">
                      <span>Risk Artırımı:</span>
                      <span className="font-medium">+{calculationResult.risk_increment} A/G</span>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-purple-200 space-y-2">
                  <p className="text-xs text-purple-600 font-medium">Denetim Türlerine Göre:</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white p-2 rounded">
                      <p className="text-lg font-bold text-purple-600">{calculationResult.initial}</p>
                      <p className="text-xs text-gray-500">İlk</p>
                    </div>
                    <div className="bg-white p-2 rounded">
                      <p className="text-lg font-bold text-blue-600">{calculationResult.surveillance}</p>
                      <p className="text-xs text-gray-500">Gözetim</p>
                    </div>
                    <div className="bg-white p-2 rounded">
                      <p className="text-lg font-bold text-green-600">{calculationResult.recertification}</p>
                      <p className="text-xs text-gray-500">Yeniden</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* TL.01 Reference Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Info className="w-4 h-4" />
                TL.01 Adam-Gün Tablosu
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Oda</TableHead>
                    <TableHead className="text-xs text-red-600">Yüksek</TableHead>
                    <TableHead className="text-xs text-yellow-600">Düşük</TableHead>
                    <TableHead className="text-xs text-green-600">Ç.Düşük</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.keys(mandayTable.high_risk).map(range => (
                    <TableRow key={range}>
                      <TableCell className="text-xs font-medium">{range}</TableCell>
                      <TableCell className="text-xs">{mandayTable.high_risk[range]}</TableCell>
                      <TableCell className="text-xs">{mandayTable.low_risk[range]}</TableCell>
                      <TableCell className="text-xs">
                        {mandayTable.extremely_low_risk[range] || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-gray-500 mt-2">
                * Her risk faktörü için +0.05 veya +0.10 A/G eklenir
              </p>
            </CardContent>
          </Card>

          {/* Calculation History */}
          {calculationHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Son Hesaplamalar</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {calculationHistory.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                      <div>
                        <p className="font-medium">{item.customer_name}</p>
                        <p className="text-xs text-gray-500">{item.room_count} oda</p>
                      </div>
                      <Badge variant="outline">{item.final_manday || item.base_manday} A/G</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManDayCalculatorPage;
